"use client";

import { cn } from "@/lib/utils";
import { getMessages } from "@/lib/messages";
import { useCurrentLocale } from "@/lib/useCurrentLocale";

/**
 * The response-time promise, in the page's locale. One source of truth
 * (`messages.responsePromise`) so the numbers can't drift between the contact
 * form, the inquiry flow, the closing CTA band and the FAQ. Client component
 * only for the locale hook; it still server-renders, so the promise is in the
 * HTML for crawlers.
 */

/** "What happens next": the three steps, for next to a form. */
export function ResponsePromiseSteps({ className }: { className?: string }) {
  const { responsePromise: p } = getMessages(useCurrentLocale());
  return (
    <div className={cn("text-left", className)}>
      <h3 className="font-display text-lg font-semibold tracking-[-0.01em] text-foreground">{p.heading}</h3>
      <ol className="mt-4 space-y-4">
        {p.steps.map((step) => (
          <li key={step.title} className="flex gap-3">
            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
            <div>
              <p className="text-sm font-semibold text-foreground">{step.title}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-foreground/70">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** The one-sentence version, for under a call-to-action button. */
export function ResponsePromiseLine({ tone = "default", className }: { tone?: "default" | "onDark"; className?: string }) {
  const { responsePromise: p } = getMessages(useCurrentLocale());
  return (
    <p className={cn("text-sm leading-relaxed", tone === "onDark" ? "text-white/70" : "text-foreground/60", className)}>
      {p.line}
    </p>
  );
}
